import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import LivePriceWidget from "@/components/LivePriceWidget";
import HalvingCountdown from "@/components/HalvingCountdown";
import FearGreedGauge from "@/components/FearGreedGauge";
import NewsCard from "@/components/NewsCard";
import NewsCardSkeleton from "@/components/NewsCardSkeleton";
import BannerAd from "@/components/BannerAd";
import BitcoinIQCTA from "@/components/BitcoinIQCTA";
import { FeaturedEventsCarousel } from "@/components/FeaturedEventsCarousel";
import { PinnedEvents } from "@/components/PinnedEvents";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MessageSquare, TrendingUp, BookOpen, Zap } from "lucide-react";
import { Link } from "react-router-dom";
// Hero image is in public folder for LCP preload discovery
const heroBg = "/images/hero-bg.webp";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const Index = () => {
  const priceWidgetRef = useScrollAnimation();
  const featuresRef = useScrollAnimation();
  const newsRef = useScrollAnimation();
  
  const { data: topNews, isLoading } = useQuery({
    queryKey: ['top-news'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data || [];
    },
    staleTime: 60000, // 1 minute
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BitcoinMind.today",
    "description": "AI-powered Bitcoin insights, real-time market data, educational guides, and comprehensive analysis platform",
    "url": "https://bitcoinmind.today",
    "publisher": {
      "@type": "Organization",
      "name": "BitcoinMind",
      "url": "https://bitcoinmind.today",
      "logo": {
        "@type": "ImageObject",
        "url": "https://lovable.dev/opengraph-image-p98pqg.png"
      },
      "sameAs": [
        "https://x.com/bitcoinmindt",
        "https://t.me/bitcoinmindt",
        "https://www.linkedin.com/company/bitcoinmind",
        "https://www.facebook.com/bitcoinmindt/"
      ]
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Bitcoin Market Data",
        "description": "Real-time Bitcoin price tracking, on-chain analytics, and whale alerts",
        "category": "Market Analysis"
      },
      {
        "@type": "Offer",
        "name": "Bitcoin Education",
        "description": "Comprehensive guides and tutorials about Bitcoin and blockchain technology",
        "category": "Education"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="BitcoinMind.today - Your AI Gateway to Everything Bitcoin"
        description="Real-time Bitcoin market data, AI-powered insights, whale alerts, on-chain analytics, educational guides, and comprehensive analysis—all in one platform."
        keywords={['bitcoin', 'cryptocurrency', 'AI bitcoin analysis', 'bitcoin news', 'bitcoin price', 'blockchain', 'bitcoin market data', 'on-chain analytics', 'whale alerts', 'bitcoin education', 'bitcoin treasuries', 'lightning network']}
        structuredData={structuredData}
      />
      <Navigation />
      
      <main>
        {/* Top Banner Ad - min-height reserves space to prevent CLS */}
        <div className="container mx-auto px-4 py-4 min-h-[282px]">
          <BannerAd size="top" />
        </div>

        {/* Hero Section - fixed height prevents CLS, contain isolates layout */}
        <section 
          className="relative py-20 overflow-hidden h-[424px]"
          style={{ contain: 'strict' }}
        >
          {/* Hero background image with fetchpriority for LCP optimization */}
          <img 
            src={heroBg} 
            alt="" 
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 0 }}
          />
          {/* Gradient overlay */}
          <div 
            className="absolute inset-0" 
            style={{ 
              background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.85))',
              zIndex: 1 
            }} 
          />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h1 className="text-5xl md:text-7xl font-bold text-white glow-text">
                Premium Bitcoin Intelligence Platform
              </h1>
              <p className="text-xl text-gray-200">
                Real-time market data, whale alerts, on-chain analytics, and AI-powered insights—all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                  <Link to="/market">
                    <TrendingUp className="mr-2 h-5 w-5" />
                    View Market Data
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/learn">
                    <BookOpen className="mr-2 h-5 w-5" />
                    Learn Bitcoin
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Price & Countdown */}
              <div ref={priceWidgetRef} className="grid md:grid-cols-2 gap-6 opacity-0">
                <LivePriceWidget />
                <HalvingCountdown />
              </div>

              {/* AI Headline of the Day */}
              <Card ref={featuresRef} className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30 opacity-0">
                <div className="flex items-start gap-4">
                  <Zap className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold mb-2">AI Headline of the Day</h2>
                    <p className="text-lg text-foreground/90">
                      Bitcoin's institutional adoption accelerates as major ETFs see record inflows, 
                      while on-chain metrics signal strong accumulation by long-term holders.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Top News */}
              <div ref={newsRef} className="opacity-0">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold">Top Bitcoin News</h2>
                  <Button asChild variant="outline">
                    <Link to="/news">View All News</Link>
                  </Button>
                </div>
                <div className="space-y-6">
                  {isLoading ? (
                    <>
                      <NewsCardSkeleton />
                      <NewsCardSkeleton />
                      <NewsCardSkeleton />
                    </>
                  ) : (
                    topNews?.map((news) => (
                      <NewsCard
                        key={news.id}
                        id={news.id}
                        headline={news.headline}
                        summary={news.summary as string[]}
                        insight={news.ai_insight}
                        source={news.source}
                        timestamp={new Date(news.published_at).toLocaleString()}
                        url={news.url}
                        imageUrl={news.image_url || undefined}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* In-Article Banner */}
              <BannerAd size="article" />
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {/* Sidebar Banner */}
              <BannerAd size="sidebar" />
              
              {/* Bitcoin IQ Quiz CTA */}
              <BitcoinIQCTA />
              
              {/* Fear & Greed */}
              <FearGreedGauge />

              {/* Quick Links */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Quick Access</h3>
                <div className="space-y-2">
                  <Button asChild variant="ghost" className="w-full justify-start">
                    <Link to="/market">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Market Dashboard
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" className="w-full justify-start">
                    <Link to="/tools">
                      <Zap className="mr-2 h-4 w-4" />
                      Top Tools
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" className="w-full justify-start">
                    <Link to="/newsletter">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Subscribe
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Featured Events Carousel */}
        <FeaturedEventsCarousel />

        {/* Pinned Events Section */}
        <PinnedEvents />
      </main>

      <Footer />
    </div>
  );
};

export default Index;
